import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { isDefinedNotEmpty, Store } from '@softline/core';
import {
  BackNavigable,
  BackNavigationService,
  showRequestErrors,
  SOFTLINE_FEATURE_TITLE,
  TitleStore } from '@softline/application';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup } from '@angular/forms';
import { Customer } from '../data/customer.model';
import { filter, map } from 'rxjs/operators';
import { RegistrationCandidate } from '../data/registration-candidate.model';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '../data/user.model';
import {
  RegistrationAdminContactsStore,
  RegistrationAdminCustomersStore,
  SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN,
  SOFTLINE_FEATURE_REGISTRATION_ADMIN_CONTACTS,
  SOFTLINE_FEATURE_REGISTRATION_ADMIN_CUSTOMERS } from '../registration-admin.shared';
import { Contact } from '../data/contact.model';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  RadioGroupDirective,
  RadioOptionComponent} from '@softline/ui-core';
import { RegistrationAdminStore } from '../store/registration-admin.store';
import { CommonModule } from '@angular/common';
import { FieldOkComponent, FieldOkResultConverter } from '@softline/dynamic';

import { CheckboxComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-registration-admin',
    imports: [CommonModule, ReactiveFormsModule, FieldOkComponent,
    CheckboxComponent,
    RadioGroupDirective,
    RadioOptionComponent,
    TranslatePipe],
    templateUrl: './registration-admin.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./registration-admin.component.css']
})
export class RegistrationAdminComponent implements OnInit, OnDestroy, BackNavigable {
  private subscription?: Subscription;
  private customerQuery$ = new BehaviorSubject<any | undefined>(undefined);

  registrationCandidate$: Observable<RegistrationCandidate> = this.store.observe(
    SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN,
    RegistrationAdminStore.getters.data
  );

  loading$ = this.store.observe(
    SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN,
    RegistrationAdminStore.getters.loading
  );

  creating$ = this.store.observe(
    SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN,
    RegistrationAdminStore.getters.getCreating
  );

  contacts$: Observable<Contact[]> = this.store.observe(
    SOFTLINE_FEATURE_REGISTRATION_ADMIN_CONTACTS,
    RegistrationAdminContactsStore.getters.all,
  );

  informationFormArray = new UntypedFormArray([
    new UntypedFormGroup({
      account: new UntypedFormControl(),
      contact: new UntypedFormControl() })
  ]);

  form = new UntypedFormGroup({
    username: new UntypedFormControl(),
    information: this.informationFormArray,
    permissions: new UntypedFormControl()
  });

  constructor(private store: Store,
              private route: ActivatedRoute,
              protected router: Router,
              protected backNavigationService: BackNavigationService) {
  }

  async ngOnInit(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#PORTAL_REGISTRATION_ADMIN.TITLE'
    );
    this.backNavigationService.set(this);

    let id = this.route.snapshot.params['id'];

    // This is a temporary fix for ids above 1000 -> generated links from backend contain a thousand separator (e.g. 1.000 instead of 1000)
    if (isDefinedNotEmpty(id) && typeof id === 'string' && id.includes('.')) {
      id = id.replace('.', '');
      await this.router.navigate(['./../', id], { relativeTo: this.route });
    }

    this.subscription = this.registrationCandidate$
      .pipe(filter(o => !!o && !!o?.email))
      .subscribe(o => {
        console.log('username: ', o);
        this.form.patchValue({ username: o?.email });
      });

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN,
        RegistrationAdminStore.actions.load,
        { pathParams: { id } }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_REGISTRATION_ADMIN_CUSTOMERS,
        RegistrationAdminCustomersStore.actions.loadMany,
        {
          clear: true }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_REGISTRATION_ADMIN_CONTACTS,
        RegistrationAdminContactsStore.actions.loadMany,
        {
          clear: true }
      );
    } catch (e) {
      console.log(e);
      showRequestErrors(this.store, e);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  addInformation(): void {
    const information = this.form.get('information') as UntypedFormArray;
    const formControl = (key: string) => ({ [key]: new UntypedFormControl() });
    const formGroup = new UntypedFormGroup({
      ...formControl('account'),
      ...formControl('contact') });
    information.push(formGroup);
  }

  removeInformation(index: number): void {
    const information = this.form.get('information') as UntypedFormArray;
    information.removeAt(index);
  }

  async onCustomerValueChange(
    customer: Customer,
    registrationCandidate: RegistrationCandidate
  ): Promise<void> {
    if (!customer) {
      return;
    }

    try {
      const query = {
        query: { accountId: customer.id, email: registrationCandidate.email } };
      const action = RegistrationAdminContactsStore.actions.loadMany;
      await this.store.dispatch(SOFTLINE_FEATURE_REGISTRATION_ADMIN_CONTACTS, action, {
        queryParams: query });
    } catch (e) {
      console.log(e);
    }
  }

  async onSubmit(user: User): Promise<void> {
    if (!user) {
      return;
    }

    try {
      const action = RegistrationAdminStore.actions.createUser;
      await this.store.dispatch(SOFTLINE_FEATURE_REGISTRATION_ADMIN_ADMIN, action, {
        user });
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#PORTAL_REGISTRATION_ADMIN.MESSAGES.SUCCESS'
      );
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        '#PORTAL_REGISTRATION_ADMIN.MESSAGES.FAILURE'
      );
    }
  }
}
